import React, { useMemo } from 'react';
import { Plus, Trash2, Eye, EyeOff, Ban, TrendingUp, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import ConceptoCombobox from '@/components/ConceptoCombobox';
import {
  BASES_LCL,
  ORDEN_GRUPO,
  UNIDAD_DE_BASE,
  formatImporte,
  round2,
} from '../../cotizaciones/utils/lclCotizacionCalc.js';

const MONEDAS = ['USD', 'COP', 'EUR', 'MXN'];

const inputCls =
  'h-8 w-full rounded border border-input bg-background px-2 text-sm ' +
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

/** Los tres estados de visibilidad y cómo se explican. */
const VISIBILIDAD = {
  visible: {
    icon: Eye,
    label: 'Se imprime',
    ayuda: 'El cliente ve la línea y su importe.',
    clase: 'text-emerald-700',
  },
  oculto_incluido: {
    icon: EyeOff,
    label: 'Cobrado, no impreso',
    ayuda: 'Suma al subtotal del grupo pero la línea no aparece en el PDF.',
    clase: 'text-amber-700',
  },
  excluido: {
    icon: Ban,
    label: 'Excluido',
    ayuda: 'Ni suma ni se imprime. Queda guardado para auditoría.',
    clase: 'text-rose-700',
  },
};
const CICLO = ['visible', 'oculto_incluido', 'excluido'];

/**
 * Tabla de conceptos de la oferta al cliente.
 *
 * Diferencias con la de cotizaciones, que son las que hacen la oferta:
 *  - una columna INTERNA con el costo del proveedor y el margen, que
 *    nunca llega al PDF;
 *  - un control de visibilidad de tres estados, para poder esconder un
 *    concepto sin dejar de cobrarlo.
 *
 * Los totales que se muestran vienen del servidor (`preview-calculo`),
 * no de un cálculo local: así lo que se ve editando es exactamente lo
 * que se guarda y se imprime.
 */
export default function OfertaConceptosTable({
  conceptos = [],
  onChange,
  calculo = null,
  monedaBase = 'USD',
  monedaSecundaria = null,
  catalogo = [],
  readOnly = false,
}) {
  const dobleMoneda = !!monedaSecundaria;

  // Los resultados del servidor llegan en el mismo orden que se enviaron.
  const lineas = calculo?.conceptos || [];
  const porGrupoCalc = useMemo(() => {
    const m = new Map();
    for (const g of calculo?.resumenGrupos || []) m.set(g.grupo, g);
    return m;
  }, [calculo]);

  const grupos = useMemo(() => {
    const mapa = new Map();
    conceptos.forEach((c, idx) => {
      const g = c.grupo || 'OTROS';
      if (!mapa.has(g)) mapa.set(g, []);
      mapa.get(g).push({ ...c, __idx: idx });
    });
    return [...mapa.entries()].sort(
      (a, b) => (ORDEN_GRUPO[a[0]] ?? 999) - (ORDEN_GRUPO[b[0]] ?? 999),
    );
  }, [conceptos]);

  const actualizar = (idx, campo, valor) =>
    onChange?.(conceptos.map((c, i) => (i === idx ? { ...c, [campo]: valor } : c)));

  /** Aplica un margen sobre el costo del proveedor de esa línea. */
  const aplicarMargen = (idx, pct) => {
    const c = conceptos[idx];
    const costo = c.costoProveedor?.tarifaUnitaria;
    if (costo == null) return;
    actualizar(idx, 'tarifaUnitaria', round2(costo * (1 + Number(pct || 0) / 100)));
  };

  /** Margen actual de la línea, deducido de la tarifa y el costo. */
  const margenDe = (c) => {
    const costo = c.costoProveedor?.tarifaUnitaria;
    if (costo == null || !costo || c.tarifaUnitaria == null) return null;
    return round2(((Number(c.tarifaUnitaria) - costo) / costo) * 100);
  };

  const ciclarVisibilidad = (idx) => {
    const actual = conceptos[idx].visibilidad || 'visible';
    const siguiente = CICLO[(CICLO.indexOf(actual) + 1) % CICLO.length];
    actualizar(idx, 'visibilidad', siguiente);
  };

  const agregar = () =>
    onChange?.([
      ...conceptos,
      {
        concepto: '',
        conceptoCodigo: null,
        grupo: 'OTROS',
        baseCobro: 'Fijo',
        cantidad: null,
        tarifaUnitaria: null,
        tarifaMinima: null,
        moneda: monedaBase,
        visibilidad: 'visible',
        origen: 'manual',
      },
    ]);

  if (conceptos.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center">
        <p className="text-sm text-muted-foreground">
          La oferta todavía no tiene conceptos.
        </p>
        {!readOnly && (
          <Button size="sm" variant="outline" className="mt-3" onClick={agregar}>
            <Plus className="mr-2 h-4 w-4" /> Agregar concepto
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[1120px] text-sm">
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="w-[38px] px-1 py-2" title="Visibilidad en el PDF" />
              <th className="px-2 py-2 text-left font-medium">Concepto</th>
              <th className="w-[130px] px-2 py-2 text-left font-medium">Catálogo</th>
              <th className="w-[120px] px-2 py-2 text-left font-medium">Base</th>
              <th className="w-[86px] px-2 py-2 text-right font-medium">Tarifa</th>
              <th className="w-[80px] px-2 py-2 text-right font-medium">Mínima</th>
              <th className="w-[70px] px-2 py-2 text-left font-medium">Mon.</th>
              <th className="w-[110px] px-2 py-2 text-right font-medium">
                Total {monedaBase}
              </th>
              {dobleMoneda && (
                <th className="w-[120px] px-2 py-2 text-right font-medium">
                  {monedaSecundaria}
                </th>
              )}
              <th className="w-[150px] border-l bg-slate-50 px-2 py-2 text-left font-medium">
                <span className="flex items-center gap-1 text-slate-600">
                  <Lock className="h-3 w-3" /> interno
                </span>
              </th>
              {!readOnly && <th className="w-[36px]" />}
            </tr>
          </thead>

          <tbody>
            {grupos.map(([grupo, filas]) => {
              const g = porGrupoCalc.get(grupo);
              return (
                <React.Fragment key={grupo}>
                  <tr className="bg-muted/30">
                    <td />
                    <td
                      colSpan={dobleMoneda ? 6 : 5}
                      className="px-2 py-1.5 text-xs font-semibold"
                    >
                      {grupo}
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs font-semibold">
                      {formatImporte(g?.subtotalBase ?? 0)}
                    </td>
                    {dobleMoneda && (
                      <td className="px-2 py-1.5 text-right text-xs font-semibold">
                        {formatImporte(g?.subtotalSecundaria)}
                      </td>
                    )}
                    <td className="border-l bg-slate-50" />
                    {!readOnly && <td />}
                  </tr>

                  {filas.map((c) => {
                    const idx = c.__idx;
                    const r = lineas[idx] || {};
                    const vis = VISIBILIDAD[c.visibilidad || 'visible'];
                    const Icono = vis.icon;
                    const margen = margenDe(c);
                    const costo = c.costoProveedor?.tarifaUnitaria;
                    const excluido = c.visibilidad === 'excluido';

                    return (
                      <tr
                        key={idx}
                        className={cn('border-t align-top', excluido && 'opacity-50')}
                      >
                        <td className="px-1 py-1.5">
                          <button
                            type="button"
                            disabled={readOnly}
                            onClick={() => ciclarVisibilidad(idx)}
                            title={`${vis.label} — ${vis.ayuda} (clic para cambiar)`}
                            className={cn(
                              'rounded p-1.5 hover:bg-muted',
                              vis.clase,
                              readOnly && 'cursor-default',
                            )}
                          >
                            <Icono className="h-4 w-4" />
                          </button>
                        </td>

                        <td className="px-2 py-1.5">
                          <input
                            className={inputCls}
                            value={c.concepto || ''}
                            placeholder="Nombre que ve el cliente"
                            readOnly={readOnly}
                            onChange={(e) => actualizar(idx, 'concepto', e.target.value)}
                          />
                          <input
                            className={cn(inputCls, 'mt-1 text-xs')}
                            value={c.descripcion || ''}
                            placeholder="Descripción (opcional)"
                            readOnly={readOnly}
                            onChange={(e) =>
                              actualizar(idx, 'descripcion', e.target.value)
                            }
                          />
                        </td>

                        <td className="px-2 py-1.5">
                          <ConceptoCombobox
                            catalogo={catalogo}
                            value={c.conceptoCodigo}
                            disabled={readOnly}
                            onSelect={(item) => {
                              onChange?.(
                                conceptos.map((x, i) =>
                                  i === idx
                                    ? {
                                        ...x,
                                        conceptoCodigo: item?.codigo || null,
                                        conceptoId: item?._id || null,
                                        grupo: item?.grupo || x.grupo,
                                        concepto: x.concepto?.trim()
                                          ? x.concepto
                                          : item?.nombre || '',
                                      }
                                    : x,
                                ),
                              );
                            }}
                          />
                        </td>

                        <td className="px-2 py-1.5">
                          <select
                            className={inputCls}
                            value={c.baseCobro || 'Fijo'}
                            disabled={readOnly}
                            onChange={(e) => actualizar(idx, 'baseCobro', e.target.value)}
                          >
                            {BASES_LCL.map((b) => (
                              <option key={b} value={b}>
                                {b}
                              </option>
                            ))}
                          </select>
                          <select
                            className={cn(inputCls, 'mt-1 text-xs')}
                            value={c.grupo || 'OTROS'}
                            disabled={readOnly}
                            onChange={(e) => actualizar(idx, 'grupo', e.target.value)}
                          >
                            {Object.keys(ORDEN_GRUPO).map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className="px-2 py-1.5">
                          <input
                            type="number"
                            step="any"
                            className={cn(inputCls, 'text-right')}
                            value={c.tarifaUnitaria ?? ''}
                            readOnly={readOnly}
                            onChange={(e) =>
                              actualizar(
                                idx,
                                'tarifaUnitaria',
                                e.target.value === '' ? null : Number(e.target.value),
                              )
                            }
                          />
                          {UNIDAD_DE_BASE[c.baseCobro] && (
                            <div className="mt-0.5 text-right text-[10px] text-muted-foreground">
                              × {r.cantidad ?? '—'} {UNIDAD_DE_BASE[c.baseCobro]}
                            </div>
                          )}
                        </td>

                        <td className="px-2 py-1.5">
                          <input
                            type="number"
                            step="any"
                            className={cn(inputCls, 'text-right')}
                            value={c.tarifaMinima ?? ''}
                            placeholder="—"
                            readOnly={readOnly}
                            onChange={(e) =>
                              actualizar(
                                idx,
                                'tarifaMinima',
                                e.target.value === '' ? null : Number(e.target.value),
                              )
                            }
                          />
                        </td>

                        <td className="px-2 py-1.5">
                          <select
                            className={inputCls}
                            value={c.moneda || monedaBase}
                            disabled={readOnly}
                            onChange={(e) => actualizar(idx, 'moneda', e.target.value)}
                          >
                            {MONEDAS.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className="px-2 py-1.5 text-right">
                          <div className="font-medium">{formatImporte(r.totalBase)}</div>
                          {r.aplicoMinimo && (
                            <Badge
                              variant="outline"
                              className="mt-1 border-amber-300 bg-amber-50 text-[10px] font-normal text-amber-800"
                            >
                              aplicó mínimo
                            </Badge>
                          )}
                        </td>

                        {dobleMoneda && (
                          <td className="px-2 py-1.5 text-right">
                            {formatImporte(r.totalSecundaria)}
                          </td>
                        )}

                        {/* Columna interna: nunca llega al PDF. */}
                        <td className="border-l bg-slate-50 px-2 py-1.5">
                          {costo != null ? (
                            <>
                              <div className="text-[11px] text-slate-600">
                                costo {formatImporte(costo)}
                              </div>
                              <div className="mt-1 flex items-center gap-1">
                                <TrendingUp className="h-3 w-3 text-slate-500" />
                                <input
                                  type="number"
                                  step="any"
                                  className="h-6 w-14 rounded border border-input bg-background px-1 text-right text-[11px]"
                                  value={margen ?? ''}
                                  placeholder="%"
                                  readOnly={readOnly}
                                  onChange={(e) => aplicarMargen(idx, e.target.value)}
                                />
                                <span className="text-[10px] text-slate-500">%</span>
                              </div>
                            </>
                          ) : (
                            <span className="text-[11px] text-slate-400">
                              sin costo de proveedor
                            </span>
                          )}
                        </td>

                        {!readOnly && (
                          <td className="px-1 py-1.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              onClick={() =>
                                onChange?.(conceptos.filter((_, i) => i !== idx))
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>

          <tfoot className="border-t-2 bg-muted/40">
            <tr>
              <td />
              <td
                colSpan={dobleMoneda ? 6 : 5}
                className="px-2 py-2 text-right font-semibold"
              >
                TOTAL
              </td>
              <td className="px-2 py-2 text-right font-semibold">
                {formatImporte(calculo?.totalBase, monedaBase)}
              </td>
              {dobleMoneda && (
                <td className="px-2 py-2 text-right font-semibold">
                  {formatImporte(calculo?.totalSecundaria, monedaSecundaria)}
                </td>
              )}
              <td className="border-l bg-slate-50 px-2 py-2 text-[11px] text-slate-600">
                {calculo?.utilidad ? (
                  <>
                    margen {formatImporte(calculo.utilidad.margenBase)}
                    <div className="text-[10px]">
                      {calculo.utilidad.margenPorcentaje} % sobre la venta
                    </div>
                  </>
                ) : (
                  '—'
                )}
              </td>
              {!readOnly && <td />}
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        {!readOnly && (
          <Button size="sm" variant="outline" onClick={agregar}>
            <Plus className="mr-2 h-4 w-4" /> Agregar concepto
          </Button>
        )}
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Lock className="h-3 w-3" />
          La columna gris es interna: el costo del proveedor y tu margen{' '}
          <strong className="mx-1">no salen en el PDF</strong> que recibe el cliente.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
        {Object.entries(VISIBILIDAD).map(([k, v]) => {
          const I = v.icon;
          return (
            <span key={k} className="flex items-center gap-1">
              <I className={cn('h-3 w-3', v.clase)} />
              <strong>{v.label}:</strong> {v.ayuda}
            </span>
          );
        })}
      </div>
    </div>
  );
}
